import { useState } from 'react';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import {
  ConversionCreditWithAmounts,
  getBestConversionCredit,
  useConversionCredits,
} from '@sorare/core/src/hooks/useConversionCredits';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

type Props = {
  sport: Sport;
  currentlyUsedConversionCredit?: ConversionCreditWithAmounts;
  canUseConversionCredit: boolean;
  amount: MonetaryAmount;
  defaultConversionCredit?: ConversionCreditWithAmounts;
  applyConversionCredits: boolean;
};

export const useConversionCreditSelector = ({
  sport,
  currentlyUsedConversionCredit,
  canUseConversionCredit,
  amount,
  defaultConversionCredit,
  applyConversionCredits,
}: Props) => {
  const {
    flags: { nonStackableMarketCredits = false },
  } = useFeatureFlags();
  const { lifecycle } = useLifecycle();
  const { conversionCredits, groupedConversionCredits, loading } =
    useConversionCredits({
      sport,
      currentlyUsedConversionCredit,
      forceRefetch: canUseConversionCredit,
    });

  const availableConversionCredits = conversionCredits?.filter(
    cc => !cc.disabled
  );

  const proposedConversionCredit = getBestConversionCredit(
    amount,
    availableConversionCredits || []
  );

  const [selectedConversionCredits, setSelectedConversionCredits] = useState<
    ConversionCreditWithAmounts[]
  >([]);

  const [conversionCredit, setConversionCredit] = useState<
    ConversionCreditWithAmounts | undefined
  >();
  const [conversionCreditInitialized, setConversionCreditInitialized] =
    useState<boolean>(false);

  if (
    !loading &&
    !conversionCreditInitialized &&
    availableConversionCredits?.length &&
    !conversionCredit &&
    lifecycle &&
    !lifecycle?.unselectConversionCreditByDefaultFromSettings
  ) {
    setConversionCreditInitialized(true);
    setConversionCredit(
      defaultConversionCredit ||
        currentlyUsedConversionCredit ||
        proposedConversionCredit
    );
    setSelectedConversionCredits(
      applyConversionCredits
        ? (conversionCredits ?? []).filter(cc => !cc.disabled) || []
        : []
    );
  }

  const conversionCreditToUse = canUseConversionCredit
    ? conversionCredit
    : undefined;

  return {
    showConversionCredit:
      amount.isPositive &&
      canUseConversionCredit &&
      availableConversionCredits &&
      availableConversionCredits?.length > 0,
    conversionCredit: conversionCreditToUse,
    conversionCredits,
    setConversionCredit,
    groupedConversionCredits,
    selectedConversionCredits:
      canUseConversionCredit && nonStackableMarketCredits
        ? selectedConversionCredits
        : [],
    setSelectedConversionCredits,
  };
};
